import { ChangeEvent, FormEvent, useState } from 'react';

interface UseFormProps<T> {
    initialValues: T;
    validate?: (values: T) => { [key: string]: string };
    onSubmit: (values: T) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function useForm<T>({
  initialValues,
  validate,
  onSubmit,
}: UseFormProps<T>) {
  const [formValues, setFormValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearErrorsForField = (fieldName: string) => {
    if (errors[fieldName]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[fieldName];
      setErrors(clearedErrors);
    }
  };

  const handleChange = (
    e: ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >,
  ): void => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    clearErrorsForField(name);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate) {
      const validationErrors = validate(formValues);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        return;
      }
    }

    setIsSubmitting(true);
    onSubmit(formValues);
    setIsSubmitting(false);
  };

  return {
    formValues,
    errors,
    isSubmitting,
    clearErrorsForField,
    handleChange,
    handleSubmit,
  };
}
